import { useEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import LinearProgress from '@mui/material/LinearProgress'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import PauseIcon from '@mui/icons-material/Pause'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { 
  useGetLastEntriesPagesInfiniteQuery,
  // useGetLastEntriesPagesInfiniteQueryState 
} from '@/api/adminServiceApi'
import { setPlateNumberHistoryParams } from '@/features/modals/modalsSlice'
import { selectActiveBuildingId } from '@/features/building/buildingSlice'
import EnteranceEventsFeedItem from './EnteranceEventsFeedItem'
import Alert from '@mui/material/Alert'

const PAGE_SIZE = 20
const MAX_AUTO_FILL_PAGES = 3

const uniqById = (entries) => {
  const seen = new Set()
  return entries.filter((entry) => {
    if (entry?.id == null) return true
    if (seen.has(entry.id)) return false
    seen.add(entry.id)
    return true
  })
}

const EnteranceEventsFeedList = ({
  channelIds,
  channelNameById = {},
  searchQuery,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const activeBuildingId = useSelector(selectActiveBuildingId);
  const [isPollingEnabled, setIsPollingEnabled] = useState(true);
  // isRefetching is true when the query is refetching and not fetching next page. 
  const [isRefetching, setIsRefetching] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  // How many pages were auto-loaded in a row to fill a non-scrollable container.
  const autoFillPagesRef = useRef(0);
  const queryParams = {
    buildingId: activeBuildingId,
    channelIds,
    size: PAGE_SIZE,
    search: searchQuery.length > 2 ? searchQuery : '',
    feed: true,
  }
  
  const {
    data: apiData,
    hasNextPage,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    isError,
    refetch,
  } = useGetLastEntriesPagesInfiniteQuery(queryParams, {
    pollingInterval: isPollingEnabled ? 15000 : 0,
    skip: !activeBuildingId,
    refetchOnFocus: false,
    refetchOnReconnect: true,
    refetchCachedPages: false,
  })
  
  const pages = apiData?.pages ?? []
  // New records shift OFFSET pages on the backend, so the same record can show up
  // at the end of one page and the start of the next one.
  const data = uniqById(pages.flatMap((page) => Array.isArray(page?.data) ? page?.data : []))
  // const isRefetching = !isLoading && isFetching && !isFetchingNextPage && !isPollingEnabled

  const canLoadNextPage = hasNextPage && !isFetchingNextPage && !isLoading && !isError

  // Scroll-triggered load: the user leaves the top, so live updates are paused.
  const loadNextPage = () => {
    if (!canLoadNextPage) return
    autoFillPagesRef.current = 0
    void fetchNextPage().then(() => {
      setIsPollingEnabled(false)
    })
  }

  const handleRefetch = () => {
    if (isRefetching) return;
    autoFillPagesRef.current = 0
    setIsRefetching(true)
    void refetch().then(() => {
      setIsPollingEnabled(true);
    }).finally(() => {
      setIsRefetching(false)
    });
  }

  const handleScrollToTopClick = () => {
    containerRef.current?.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
    handleRefetch();
  }

  const handleScroll = (event) => {
    const scrollTargetTop = 40;
    const { scrollTop, scrollHeight, clientHeight } = event.target;
    const widgetTopSnaped = document.scrollingElement.scrollTop < scrollTargetTop;
    if (widgetTopSnaped){
      // scroll whole page to bottom
      window.scrollTo({
        left: 0,
        top: scrollTargetTop,
        behavior: "smooth", 
      });
    } 
    if (scrollTop + clientHeight >= scrollHeight - 100) {
      loadNextPage()
    }
    if (scrollTop === 0){
      handleRefetch();
    }
  }

  useEffect(() => {
    // Pages shrink after duplicate recognitions are merged, so the container may
    // not be scrollable yet. Fill it with a few pages, but never chain through the
    // whole feed — further pages load on scroll only. Polling stays enabled.
    const el = containerRef.current;
    if (!el || !canLoadNextPage) return;
    const isScrollable = el.scrollHeight > el.clientHeight;
    if (isScrollable || autoFillPagesRef.current >= MAX_AUTO_FILL_PAGES) return;
    autoFillPagesRef.current += 1
    void fetchNextPage()
  }, [canLoadNextPage, data.length]);

  return (
    <Box 
      sx={{ 
        flex: '1 1',
        position: 'relative',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      { !isPollingEnabled && !isRefetching && <EventsFeedPauseOverlay onScrollToTop={handleScrollToTopClick} /> }
      {
        !isLoading && isFetching && !isFetchingNextPage && (
          <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0 }} />
        )
      }
      <Box 
        ref={containerRef}
        onScroll={handleScroll}
        sx={{ 
          height: '100%',
          overflowY: 'scroll',
          scrollBehavior: 'smooth',
          px: 1.5,
          scrollPaddingBottom: '96px',
        }}
      >
        <Stack 
          spacing={1.25} 
          sx={{ 
            py: 1,
          }}
        >
          {!isLoading && data.length === 0 && (
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                py: 2,
                textAlign: 'center'
              }}>
              {t('enterance-events-feed-list_no-data')}
            </Typography>
          )}
          { // OnscrollTop and first page loading skeleton
            isRefetching && <ChatListSkeleton count={1}/>
          }
          {
            !isLoading && data.map((entry) => (
              <EnteranceEventsFeedItem
                key={`${entry.id}|`}
                entry={entry}
                channelName={channelNameById[entry.channel_id]}
                onClick={() => dispatch(
                  setPlateNumberHistoryParams({
                    buildingId: activeBuildingId,
                    plateNumber: entry.plate_number,
                    ...(channelIds?.length ? { channelIds } : {}),
                  })
                )}
              />
            ))
          }
          { canLoadNextPage && (
            // Fallback when the container is still not scrollable after auto-fill.
            <Button size="small" onClick={loadNextPage}>
              {t('enterance-events-feed-list_load-more')}
            </Button>
          )}
          { (!hasNextPage && !isLoading && !isError) && <EndOfListAlert /> }
          { isError && !isFetchingNextPage && (
            <Alert
              severity="error"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => void (pages.length ? fetchNextPage() : refetch())}
                >
                  {t('reload')}
                </Button>
              }
            >
              {t('loading_error')}
            </Alert>
          )}
          { (isFetchingNextPage || isLoading) && <ChatListSkeleton /> }
        </Stack>
      </Box>
    </Box>
  );
}

const ChatListSkeleton = ({count = 4}) => Array.from({ length: count }).map((_, index) => (
  <Paper
    key={index}
    elevation={0}
    sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
  >
    <Skeleton width="40%" height={16} sx={{ mb: 1 }} />
    {/* <Skeleton width="60%" height={24} sx={{ mb: 1 }} /> */}
    <Stack direction="row" spacing={1}>
      <Skeleton width={72} height={28} />
      <Skeleton width={88} height={28} />
      <Skeleton width={64} height={28} />
    </Stack>
  </Paper>
))

const EndOfListAlert = () => {
  const { t } = useTranslation();
  return <Alert severity="info">
    { t('end-of-list') }
  </Alert>
}
const EventsFeedPauseOverlay = ({ onScrollToTop }) => {
  const { t } = useTranslation();
  return (
    <Stack
      direction="row"
      spacing={1}
      sx={[{
        justifyContent: "space-between",
        alignItems: "center"
      }, (theme) => ({
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1,
        px: 1.5,
        py: 1, 
        height: '48px',
        backdropFilter: 'blur(3px)',
        backgroundColor: theme.palette.background.blur,
      })]}>
      <Stack
        direction="row"
        spacing={1}
        sx={[{
          alignItems: "center"
        }, (theme) => ({
          color: theme.palette.text.secondary,
          cursor: 'default',
        })]}>
        <PauseIcon 
          fontSize="small"
          color="inherit"
        />
        <Typography 
          variant="caption" 
          sx={{
            fontWeight: 600,
          }}
        >
          {t('enterance-events-feed-list_update-stoped')}
        </Typography>
      </Stack>
      <Button
        size="small"
        variant="outlined"
        startIcon={<ArrowUpwardIcon />}
        onClick={onScrollToTop}
      >
        {t('enterance-events-feed-list_to-top')}
      </Button>
    </Stack>
  );
}
export default EnteranceEventsFeedList
